class StickyHeader extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.header = document.querySelector('.section-header');
        this.promo = document.querySelector('.announcement-bar-section');
        this.stickyType = this.getAttribute('data-sticky-type');
        this.headerIsAlwaysSticky = this.stickyType === 'always' ||
            this.stickyType === 'reduce-logo-size';
        this.headerBounds = {};
        this.isStuck = false;

        this.setHeaderHeight();
        this.observeHeaderHeight();

        window.matchMedia('(max-width: 990px)').addEventListener(
            'change',
            this.setHeaderHeight.bind(this)
        );

        if (this.headerIsAlwaysSticky && this.stickyType !== 'reduce-logo-size') {
            this.header.classList.add('shopify-section-header-sticky');
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearch = this.querySelector('predictive-search');

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => this.preventReveal = true;

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, false);

        if (this.stickyType === 'reduce-logo-size') {
            this.onScroll();
        } else {
            this.createObserver();
        }
    }

    setHeaderHeight() {
        const promoHeight = this.promo ? this.promo.offsetHeight : 0;
        const headerHeight = this.offsetHeight;

        this.promoHeight = promoHeight;

        document.documentElement.style.setProperty('--promo-height', `${promoHeight}px`);
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        this.syncStuckSpacer();
        this.updateChromeHeight();
    }

    syncStuckSpacer() {
        if (!this.header || this.stickyType !== 'reduce-logo-size') return;

        if (this.isStuck) {
            this.header.style.minHeight = `${this.offsetHeight}px`;
        } else {
            this.header.style.minHeight = '';
        }
    }

    setStuckState(isStuck) {
        if (this.isStuck === isStuck) {
            this.syncStuckSpacer();
            return;
        }

        this.isStuck = isStuck;
        this.classList.toggle('is-stuck', isStuck);
        this.header.classList.toggle('scrolled-past-header', isStuck);
        this.syncStuckSpacer();
        this.updateChromeHeight();
    }

    updateChromeHeight() {
        const promoHeight = this.promoHeight || 0;
        const headerHeight = this.offsetHeight;

        document.documentElement.style.setProperty(
            '--header-chrome-height',
            `${this.isStuck ? headerHeight : promoHeight + headerHeight}px`
        );
    }

    observeHeaderHeight() {
        if (this.headerHeightObserver) return;

        this.headerHeightObserver = new ResizeObserver(() => {
            this.setHeaderHeight();
        });
        this.headerHeightObserver.observe(this);

        if (this.promo) {
            this.headerHeightObserver.observe(this.promo);
        }
    }

    disconnectedCallback() {
        if (this.headerHeightObserver) {
            this.headerHeightObserver.disconnect();
        }
        this.removeEventListener(
            'preventHeaderReveal',
            this.hideHeaderOnScrollUp
        );
        window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
        let observer = new IntersectionObserver((entries, observer) => {
            this.headerBounds = entries[0].intersectionRect;
            observer.disconnect();
        })

        observer.observe(this.header);
    }

    onScroll() {
        const scrollTop = window.pageYOffset ||
            document.documentElement.scrollTop;

        if (this.header.classList.contains('search-open')) return;
        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

        if (this.stickyType === 'reduce-logo-size') {
            if (document.documentElement.classList.contains('header-menu-scroll-lock')) {
                this.currentScrollTop = scrollTop;
                return;
            }

            const promoHeight = this.promoHeight || 0;
            this.setStuckState(scrollTop >= promoHeight);
            this.currentScrollTop = scrollTop;
            return;
        }

        if (scrollTop > this.currentScrollTop &&
            scrollTop > this.headerBounds.bottom
        ) {
            this.header.classList.add('scrolled-past-header');

            if (this.preventHide) return;

            requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop &&
            scrollTop > this.headerBounds.bottom
        ) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
                requestAnimationFrame(this.reveal.bind(this));
            } else {
                window.clearTimeout(this.isScrolling);

                this.isScrolling = setTimeout(() => {
                    this.preventReveal = false;
                }, 66);

                requestAnimationFrame(this.hide.bind(this));
            }
        } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
    }

    hide() {
        if (this.headerIsAlwaysSticky) return;

        this.header.classList.add(
            'shopify-section-header-hidden',
            'shopify-section-header-sticky'
        );
        this.closeMenuDisclosure();
        this.closeSearchModal();
    }

    reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove(
            'shopify-section-header-hidden',
            'shopify-section-header-sticky',
            'animate'
        );
    }

    closeMenuDisclosure() {
        this.disclosures = this.disclosures ||
            this.header.querySelectorAll('header-menu');
        this.disclosures.forEach(disclosure => disclosure.close());
    }

    closeSearchModal() {
        this.searchModal = this.searchModal ||
            this.header.querySelector('details-modal');
        this.searchModal?.close(false);

        const predictiveSearch = this.querySelector('predictive-search');
        if (predictiveSearch?.closeSearch && this.header.classList.contains('search-open')) {
            predictiveSearch.closeSearch();
        }
    }
}

customElements.define('sticky-header', StickyHeader);
